import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/tmp/48c88558/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3 {...{
      "id": "ibm-firmly-believes-that-web-and-software-experiences-should-be-accessible-for-everyone-regardless-of-abilities-or-impairments-carbon-is-committed-to-following-and-complying-with-best-practices-when-it-comes-to-accessibility"
    }}>{`IBM firmly believes that web and software experiences should be accessible for everyone, regardless of abilities or impairments. Carbon is committed to following and complying with best practices when it comes to accessibility.`}</h3>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Carbon and accessibility</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Categories of disabilities</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Global accessibility standards</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Resources</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Tools</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "carbon-and-accessibility"
    }}>{`Carbon and accessibility`}</h2>
    <p>{`Accessible design not only helps users with disabilities; it provides better user experiences for everyone.
All Carbon components follow the `}<a parentName="p" {...{
        "href": "https://www.ibm.com/able/guidelines/ci162/accessibility_checklist.html"
      }}>{`IBM Accessibility Checklist`}</a>{` which is based on WCAG AA, Section 508, and European standards. Carbon’s patterns are perceivable, operable, and understandable to users, even when using a screen reader or other assistive technology.`}</p>
    <p>{`However, how you use Carbon also affects the accessibility of a product. Read this page to learn about accessibility in general. Then, while you’re working, use the role-specific Carbon accessibility guidelines to help you make products that anyone can use.`}</p>
    <p>{`For a product to be considered accessible, it should:`}</p>
    <ul>
      <li parentName="ul">{`Give every user the same benefits, regardless of ability`}</li>
      <li parentName="ul">{`Adapt to any user in any context`}</li>
    </ul>
    <h2 {...{
      "id": "categories-of-disabilities"
    }}>{`Categories of disabilities`}</h2>
    <p>{`Disabilities can be thought of as a mismatch between one’s ability and one’s environment, and can be broken into three main types:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Situational`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A person with typical vision might struggle to view their screen in a bright environment. Or, a person sitting in a library may be unable to watch a video with the audio turned on.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Temporary`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A person with a broken wrist may not be able to type but will regain the ability when healed.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Long-lasting`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This is what people typically think of when they hear “disability.”`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "blind-users"
    }}>{`Blind users`}</h3>
    <h4 {...{
      "id": "how-they-experience-an-interface"
    }}>{`How they experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`May use a screen reader to experience interfaces`}</li>
      <li parentName="ul">{`May rely on Braille output`}</li>
      <li parentName="ul">{`Cannot be expected to use a pointer or mouse for input`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Is visual information translated effectively into text? Can the image be understood through its metadata alone?`}</li>
      <li parentName="ul">{`When possible, test all designs through a screen reader.`}</li>
    </ul>
    <h4 {...{
      "id": "how-this-applies-to-everyone"
    }}>{`How this applies to everyone`}</h4>
    <ul>
      <li parentName="ul">{`As audio-only interfaces gain popularity through devices like AI assistants, users are expecting more and more from the audio representations of experiences.`}</li>
    </ul>
    <h3 {...{
      "id": "low-vision-users"
    }}>{`Low-vision users`}</h3>
    <p>{`Low vision can include partial sight in one or both eyes and can range from mild to severe. It affects approximately 4% of the world’s population.`}</p>
    <h4 {...{
      "id": "how-they-experience-an-interface-1"
    }}>{`How they experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`May use screen readers, screen magnifiers, high-contrast modes, and/or monochrome displays`}</li>
      <li parentName="ul">{`May have their browser font size adjusted to a larger setting`}</li>
      <li parentName="ul">{`May not use adaptive technology at all`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about-1"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Maximize the readability and visual clarity of content.`}</li>
      <li parentName="ul">{`Consider how relative proximity of information changes when a page is magnified.`}</li>
      <li parentName="ul">{`Follow `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/keyboard-accessibility/"
        }}>{`keyboard guidelines`}</a>{` and test with a screen reader to ensure the page is read to the user in a logical order.`}</li>
      <li parentName="ul">{`To get a better understanding of the various low-vision disabilities, we recommend using the `}<a parentName="li" {...{
          "href": "https://chrome.google.com/webstore/detail/nocoffee/jjeeggmbnhckmgdhmgdckeigabjfbddl"
        }}>{`NoCoffee`}</a>{` Chrome plugin to preview websites.`}</li>
    </ul>
    <h4 {...{
      "id": "how-this-applies-to-everyone-1"
    }}>{`How this applies to everyone`}</h4>
    <ul>
      <li parentName="ul">{`Users without disabilities sometimes need to view screens in poor lighting conditions. For example, imagine using a screen outside on a bright day. A higher-contrast design will make the screen more usable for everyone.`}</li>
      <li parentName="ul">{`Vision worsens gradually starting around age 40, and good contrast helps this very large demographic use your interface.`}</li>
    </ul>
    <h3 {...{
      "id": "color-blind-users"
    }}>{`Color-blind users`}</h3>
    <p>{`Color-blindness affects 8% of all men and 0.4% of women.`}</p>
    <h4 {...{
      "id": "how-they-experience-an-interface-2"
    }}>{`How they experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`Will not be able to differentiate between some colors on an interface`}</li>
      <li parentName="ul">{`Rely on non-color information to use an interface`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about-2"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Run your design through a color-blind simulator. If the design doesn’t work, try another approach. If you’re working in Sketch, we recommend the `}<a parentName="li" {...{
          "href": "http://www.getstark.co/"
        }}>{`Stark`}</a>{` plugin.`}{
          /* - To view best practices for using color in Data Visualization, view the [Data Vis](/data-visualization/overview/colors) color page. */
        }</li>
    </ul>
    <h3 {...{
      "id": "deaf-and-hard-of-hearing-users"
    }}>{`Deaf and hard-of-hearing users`}</h3>
    <h4 {...{
      "id": "how-they-experience-an-interface-3"
    }}>{`How they experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`May rely on captioning and other alternative representations of audio`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about-3"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Find alternatives to conveying information exclusively with sound.`}</li>
      <li parentName="ul">{`Transcribe and caption all videos and animations that have meaningful audio.`}</li>
    </ul>
    <h4 {...{
      "id": "how-this-applies-to-everyone-2"
    }}>{`How this applies to everyone`}</h4>
    <ul>
      <li parentName="ul">{`All users can benefit from closed captioning. Imagine using your device in a loud environment or, alternatively, in a quiet environment when it wouldn’t be appropriate to turn your sound on.`}</li>
    </ul>
    <h3 {...{
      "id": "physical-disabilities"
    }}>{`Physical disabilities`}</h3>
    <h4 {...{
      "id": "how-users-with-physical-disabilities-experience-an-interface"
    }}>{`How users with physical disabilities experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`May rely on keyboards, track balls, voice recognition, and other assistive technologies to interact with an interface`}</li>
      <li parentName="ul">{`May not be able to use a keyboard, mouse, or other pointer`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about-4"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Design for good keyboard interaction, making sure all actions are keyboard accessible and efficient.`}</li>
      <li parentName="ul">{`Learn how to navigate using a `}<a parentName="li" {...{
          "href": "https://help.gnome.org/users/gnome-help/stable/keyboard-nav.html.en"
        }}>{`keyboard`}</a>{` and spend some time navigating the web, email, and digital products using only the keyboard.`}</li>
    </ul>
    <h4 {...{
      "id": "how-this-applies-to-everyone-3"
    }}>{`How this applies to everyone`}</h4>
    <ul>
      <li parentName="ul">{`Many users prefer to navigate interfaces with a keyboard and no mouse for efficiency. Good keyboard navigation can help everyone be more productive.`}</li>
    </ul>
    <h3 {...{
      "id": "users-with-cognitive-disabilities"
    }}>{`Users with cognitive disabilities`}</h3>
    <p>{`These users offer a broad spectrum of concerns for design. Functional cognitive disabilities can include difficulty with:`}</p>
    <ul>
      <li parentName="ul">{`Memory`}</li>
      <li parentName="ul">{`Problem solving`}</li>
      <li parentName="ul">{`Attention`}</li>
      <li parentName="ul">{`Reading, linguistic, and verbal comprehension`}</li>
      <li parentName="ul">{`Math comprehension`}</li>
      <li parentName="ul">{`Visual comprehension`}</li>
    </ul>
    <h4 {...{
      "id": "how-they-experience-an-interface-4"
    }}>{`How they experience an interface`}</h4>
    <ul>
      <li parentName="ul">{`May have limited working memory and need information to remain visible throughout the completion of a task`}</li>
      <li parentName="ul">{`May experience seizures when exposed to flashing content (epilepsy)`}</li>
    </ul>
    <h4 {...{
      "id": "what-designers-should-think-about-5"
    }}>{`What designers should think about`}</h4>
    <ul>
      <li parentName="ul">{`Designers should understand the danger of complex language, non-stopping or flashing animations and fundamental cognitive usability heuristics.`}</li>
      <li parentName="ul">{`Design in as linear a fashion as possible and focus on design heuristics that have to do with cognitive load and memory.`}</li>
    </ul>
    <h4 {...{
      "id": "how-this-applies-to-everyone-4"
    }}>{`How this applies to everyone`}</h4>
    <ul>
      <li parentName="ul">{`Best practices for cognitive disabilities benefit all users. Busy environments can tax your cognitive load. Aging adults may also experience a decline in cognitive abilities. Placing a low cognitive load on users reduces mistakes and improves effectiveness, regardless of their abilities.`}</li>
    </ul>
    <h2 {...{
      "id": "global-accessibility-standards"
    }}>{`Global accessibility standards`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/"
        }}>{`World Wide Web Consortium`}</a>{` (W3C)‘s Web Accessibility Initiative (WAI) is an effort to improve the accessibility of the World Wide Web for people of all abilities.`}</li>
      <li parentName="ul">{`Web Accessibility Initiative (WAI) contributors create and maintain `}<a parentName="li" {...{
          "href": "https://www.w3.org/TR/WCAG21/"
        }}>{`Web Content Accessibility Guidelines`}</a>{` (WCAG), which is the global accessibility standard.`}</li>
    </ul>
    <h2 {...{
      "id": "resources"
    }}>{`Resources`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.ibm.com/able/guidelines/ci162/accessibility_checklist.html"
        }}>{`IBM Web Accessibility Checklist`}</a>{` - IBM’s specific ruleset for creating accessible web applications`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://w3.ibm.com/able/devtest/quick/"
        }}>{`IBM accessibility quick guidance`}</a>{` - (Internal only)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/low-vision-needs/"
        }}>{`W3C low vision requirements`}</a></li>
    </ul>
    <h2 {...{
      "id": "tools"
    }}>{`Tools`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://chrome.google.com/webstore/detail/high-contrast/djcfdncoelnlbldjfhinnjlhdjlikmph/related?hl=en"
        }}>{`High-contrast Chrome plugin`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.getstark.co/"
        }}>{`Stark Sketch plugin`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://chrome.google.com/webstore/detail/nocoffee/jjeeggmbnhckmgdhmgdckeigabjfbddl"
        }}>{`NoCoffee vision simulator`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://marijohannessen.github.io/color-contrast-checker/"
        }}>{`Color contrast checker`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      